import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Building2,
  Package,
  UserCog,
  ShoppingCart,
  BoxesIcon,
  Truck,
  Calculator,
  FileText,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { useState } from 'react';

interface MenuItem {
  label: string;
  icon: ReactNode;
  href?: string;
  children?: { label: string; href: string }[];
}

import { ReactNode } from 'react';

const menuItems: MenuItem[] = [
  {
    label: 'ダッシュボード',
    icon: <LayoutDashboard size={20} />,
    href: '/',
  },
  {
    label: 'マスタ管理',
    icon: <Building2 size={20} />,
    children: [
      { label: '仕入先', href: '/masters/suppliers' },
      { label: '売り先', href: '/masters/customers' },
      { label: '商品', href: '/masters/products' },
      { label: 'ブランド', href: '/masters/brands' },
      { label: '冷蔵庫', href: '/masters/cold-storages' },
      { label: '担当者', href: '/masters/staff' },
    ],
  },
  {
    label: '仕入管理',
    icon: <ShoppingCart size={20} />,
    href: '/purchases',
  },
  {
    label: '在庫管理',
    icon: <BoxesIcon size={20} />,
    children: [
      { label: 'チルド在庫', href: '/inventory/chilled' },
      { label: '冷凍在庫', href: '/inventory/frozen' },
      { label: '倉庫別在庫', href: '/inventory/by-storage' },
    ],
  },
  {
    label: '出庫管理',
    icon: <Truck size={20} />,
    children: [
      { label: 'チルド出庫', href: '/shipments/chilled' },
      { label: '冷凍出庫', href: '/shipments/frozen' },
    ],
  },
  {
    label: '精算管理',
    icon: <Calculator size={20} />,
    href: '/settlements',
  },
  {
    label: '請求管理',
    icon: <FileText size={20} />,
    href: '/invoices',
  },
  {
    label: 'ユーザー管理',
    icon: <UserCog size={20} />,
    href: '/admin/users',
  },
];

export default function Sidebar() {
  const location = useLocation();
  const [openMenus, setOpenMenus] = useState<string[]>(['マスタ管理', '在庫管理', '出庫管理']);

  const toggleMenu = (label: string) => {
    setOpenMenus(prev =>
      prev.includes(label)
        ? prev.filter(l => l !== label)
        : [...prev, label]
    );
  };

  const isActive = (href: string) => location.pathname === href;
  const isParentActive = (children?: { label: string; href: string }[]) =>
    children?.some(child => location.pathname === child.href);

  return (
    <aside
      className="w-[200px] h-screen fixed left-0 top-0 flex flex-col z-50"
      style={{
        backgroundColor: 'var(--background-aside-primary)',
        color: 'var(--aside-text-color)',
        borderRight: 'var(--border-aside)',
      }}
    >
      {/* Logo */}
      <div className="p-4 flex items-center gap-3 border-b border-white/10">
        <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
          <Package size={24} className="text-white" />
        </div>
        <div>
          <h1 className="font-bold text-sm leading-tight">フードクリエイト</h1>
          <p className="text-xs opacity-75">在庫管理システム</p>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto">
        {menuItems.map((item) => (
          <div key={item.label}>
            {item.children ? (
              <>
                <button
                  onClick={() => toggleMenu(item.label)}
                  className="w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors hover:bg-white/10"
                  style={{
                    backgroundColor: isParentActive(item.children) ? 'rgba(255,255,255,0.1)' : 'transparent',
                  }}
                >
                  <span className="flex items-center gap-3">
                    <span style={{ color: 'var(--aside-icon)' }}>{item.icon}</span>
                    <span>{item.label}</span>
                  </span>
                  {openMenus.includes(item.label) ? (
                    <ChevronDown size={16} />
                  ) : (
                    <ChevronRight size={16} />
                  )}
                </button>
                {openMenus.includes(item.label) && (
                  <div className="bg-black/10">
                    {item.children.map((child) => (
                      <Link
                        key={child.href}
                        to={child.href}
                        className="block pl-12 pr-4 py-2 text-sm transition-colors"
                        style={{
                          backgroundColor: isActive(child.href) ? 'var(--background-active)' : 'transparent',
                          color: isActive(child.href) ? 'var(--text-primary)' : 'inherit',
                        }}
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <Link
                to={item.href!}
                className="flex items-center gap-3 px-4 py-2.5 text-sm transition-colors"
                style={{
                  backgroundColor: isActive(item.href!) ? 'var(--background-active)' : 'transparent',
                  color: isActive(item.href!) ? 'var(--text-primary)' : 'inherit',
                }}
              >
                <span style={{ color: isActive(item.href!) ? 'var(--accent-color)' : 'var(--aside-icon)' }}>
                  {item.icon}
                </span>
                <span>{item.label}</span>
              </Link>
            )}
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-white/10 text-xs opacity-60">
        <p>v1.0.0</p>
      </div>
    </aside>
  );
}

